<template>
  <div class="flex flex-col gap-1 w-full" v-if="showBottom">
    <div>
      <MissionEditBottomSeason
        :dispatch-mission="dispatchMission"
        :leader-mate="leaderMate"
        :is-edit="isEdit"
        :is-world-mission="isWorldMission"
      />
    </div>
    <div class="text-base-content/70" v-if="!isWorldMission">
      <span class="font-bold text-base-content/90">
        {{ $t(`mission.gain`) }}:
      </span>
      {{
        dispatchMission.type === DispatchType.Mission
          ? $t(`mission.expectedGain`, {
              count: dispatchMission.duration! / 2,
              countEquipment: equipmentCount[dispatchMission.duration!],
            })
          : $t(`mission.expectedGainStroll`, {
              count: 1,
            })
      }}
    </div>
    <div class="flex items-center gap-2 mt-1">
      <div class="flex items-center gap-1" v-if="!isWorldMission">
        <div>{{ $t(`mission.dispatchDuration`) }}</div>
        <VanStepper
          v-if="dispatchMission.type === DispatchType.Mission"
          v-model="duration"
          :disabled="!isEdit"
          disable-input
          step="2"
          min="2"
          max="12"
          input-width="24px"
          button-size="20px"
        />
        <div v-else>5</div>
        <div>{{ $t(`Time.minute`) }}</div>
      </div>

      <button
        class="btn btn-primary btn-xs flex-1 rounded"
        @click="clickDispatch"
      >
        {{ hasFriendMate ? $t(`mission.inviteFriend`) : $t(`mission.exec`) }}

        <template v-if="!userStore.isFreeModel()">
          <PowerIcon />
          {{ userStore.getPowerCostByModel(totalPower) }}
        </template>
      </button>

      <SelectModelBtn />
    </div>
  </div>
</template>

<script setup lang="ts">
import { DispatchType } from "@/enums/mission";
import { useGlobalModal } from "@/stores/globalModal";
import { useMission } from "@/stores/mission";
import { useMate } from "@/stores/mate";
import { Mate, MateDispatch, MateDispatchMember } from "@/types/entity";
import { showConfirmDialog, showFailToast, showLoadingToast } from "vant";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { StorageRemove } from "@/tools/localStorage";
import { useGlobalUser } from "@/stores/globalUser";
import { useRouter } from "vue-router";
import { ApiMateDispatchParams } from "@/apis/dispatchMission";
import { AxiosError } from "axios";
import { generateObjective } from "./MissionGenerate";
import { fixNumber } from "./MissionStat";
import { DispatchMemberPayerType } from "@/enums/mateEnums";

const { t } = useI18n();

const missionStore = useMission();
const mateStore = useMate();
const userStore = useGlobalUser();
const router = useRouter();

const props = defineProps<{
  dispatchMission: MateDispatch & { objectiveSignature: string };
  leaderMate?: Mate;
  isEdit: boolean;
  forceShow?: boolean;
  isWorldMission?: boolean;
}>();

const showBottom = computed(() => {
  return (
    props.forceShow ||
    (props.leaderMate?.id &&
      (props.dispatchMission.type === DispatchType.Stroll ||
        (props.dispatchMission.type === DispatchType.Mission &&
          props.dispatchMission.mission &&
          props.dispatchMission.location)))
  );
});

const hasFriendMate = computed(() => {
  return props.dispatchMission.dispatchMemberzz?.some(
    (v) => !mateStore.isMyMateById(v.mate!.id)
  );
});

const duration = computed({
  get: () => props.dispatchMission.duration,
  set: (value) => {
    const { dispatchMission } = props;
    dispatchMission.duration = value;
  },
});

const equipmentCount = {
  2: "0~1",
  4: "1",
  5: "1~2",
  6: "1~2",
  8: "2",
  10: "2~3",
  12: "3",
};

const totalPower = computed(() => {
  if (props.isWorldMission) return 10;
  const total = missionStore.totalCostPower(props.dispatchMission);
  let payCount = 1;

  props.dispatchMission.dispatchMemberzz?.forEach((v) => {
    if (v.payerType === DispatchMemberPayerType.Host) {
      payCount++;
    }
  });

  return fixNumber(total * payCount);
});

const modalStore = useGlobalModal();
const clickDispatch = async () => {
  const isMission = props.dispatchMission.type === DispatchType.Mission;

  if (!props.leaderMate?.id)
    return showFailToast(t(`mission.mate.selectFirst`));
  if (isMission) {
    const { location, mission } = props.dispatchMission;
    if (!location) return showFailToast(t(`mission.location.selectFirst`));
    if (!mission) return showFailToast(t(`mission.mission.selectFirst`));
  }

  if (
    modalStore.shouldOpenPowerNotEnoughModal({
      spentPowerAmount: totalPower.value,
      cb: clickDispatch,
      plusModelRatio: true,
    })
  )
    return;

  if (
    props.dispatchMission.isRankingEnabled &&
    !props.dispatchMission.objective
  ) {
    showConfirmDialog({
      title: "提示",
      message: `还没有评估任务目标哦，需要帮您自动填写一个简单任务难度的任务目标吗？${getCostText(
        1
      )}`,
      confirmButtonText: "确定",
      cancelButtonText: "取消",
    }).then(async () => {
      generateObjective(props.dispatchMission);
    });
    return;
  }

  const loading = showLoadingToast(t(`Message.loading`));
  try {
    const {
      mission,
      location,
      duration,
      objective,
      dispatchMemberzz,
      carriedEquipmentzz,
      carriedJournalzz,
      carriedMailzz,
      carriedDispatchzz,
      carriedMemoryzz,

      objectiveSignature,
      difficultyLevel,
      difficultyLevelReason,
      isRankingEnabled,
      dispatchTemplateId,
    } = props.dispatchMission;

    let params: ApiMateDispatchParams = isMission
      ? {
          type: DispatchType.Mission,
          missionId: mission!.id,
          locationId: location!.id,
          duration: duration!,
          objective,
          memberzz: dispatchMemberzz?.map(
            (v) =>
              ({
                mateId: v.mate!.id,
                payerType: v.payerType,
              } as MateDispatchMember)
          ),
          carriedEquipmentIdzz: carriedEquipmentzz?.map((v) => v.id),
          carriedJournalIdzz: carriedJournalzz?.map((v) => v.id),
          carriedMailIdzz: carriedMailzz?.map((v) => v.id),
          carriedDispatchIdzz: carriedDispatchzz?.map((v) => v.id),
          carriedMemoryIdzz: carriedMemoryzz?.map((v) => v.id),
          isRankingEnabled,
          dispatchTemplateId,
        }
      : {
          type: DispatchType.Stroll,
          duration: 5,
          carriedEquipmentIdzz: carriedEquipmentzz?.map((v) => v.id),
        };

    if (difficultyLevel) {
      params.difficultyLevel = difficultyLevel;
      params.difficultyLevelReason = difficultyLevelReason;
      params.objectiveSignature = objectiveSignature;
    }

    params.mateId = props.leaderMate!.id;

    if (props.dispatchMission.id) {
      await missionStore.updateMateDispatch(props.dispatchMission.id, params);
    } else {
      await missionStore.createMateDispatch(params);
    }
    StorageRemove(`dispatchMission:${props.dispatchMission.type}`);
    await userStore.refresh();
    router.replace({
      name: "missionHome",
    });
    loading.close();
  } catch (e) {
    console.log(e);
    const error = e as AxiosError<{ message: string }>;
    if (error?.response?.data?.message) {
      const msg = error.response.data.message;
      if (msg === "Difficulty level is required when ranking is enabled.") {
        showConfirmDialog({
          title: "提示",
          message: `还没有评估任务目标哦，需要帮您自动填写一个简单任务难度的任务目标吗？${getCostText(
            1
          )}`,
          confirmButtonText: "确定",
          cancelButtonText: "取消",
        }).then(async () => {
          generateObjective(props.dispatchMission);
        });
      } else if (msg === "任务目标已变更，请重新评估任务难度") {
        showConfirmDialog({
          title: "提示",
          message: `检测到任务目标已修改过，需要重新评估任务难度吗？${getCostText(
            1
          )}`,
          confirmButtonText: "确定",
          cancelButtonText: "取消",
        }).then(async () => {
          generateObjective(props.dispatchMission);
        });
      } else if (
        msg === "当 difficulty level 存在时 objective signature 不能为空。"
      ) {
        showConfirmDialog({
          title: "提示",
          message: `该任务目标还未进行过评估，需要帮您自动评估任务难度吗？${getCostText(
            1
          )}`,
          confirmButtonText: "确定",
          cancelButtonText: "取消",
        }).then(async () => {
          generateObjective(props.dispatchMission);
        });
      }

      // showFailToast(error.response.data.message);
    }
    loading.close();
  }
};

const getCostText = (amount: number) => {
  if (userStore.isFreeModel()) return "";
  return `（消耗 ${userStore.getPowerCostByModel(amount)} 能量块）`;
};
</script>

<style scoped lang="postcss">
.highest,
.high {
  @apply text-success;
}

.medium {
  @apply text-info;
}

.low,
.lowest {
  @apply text-error;
}
</style>
